import React from "react";
import { ReactComponent as MailMark } from "../../../assets/svg/plan/mail.svg";
import { ReactComponent as WhatsappMark } from "../../../assets/svg/plan/whatsapp.svg";
import { ReactComponent as CallMark } from "../../../assets/svg/plan/call.svg";
import { SUPPORT_CHANNELS, SupportChannel } from "../types/brand.types";

/**
 * The drawn marks, each authored at 18px with its stroke already in ink-muted,
 * so nothing that places this list has to colour them.
 */
const CHANNEL_ICONS: Record<SupportChannel, React.ElementType> = {
  EMAIL: MailMark,
  WHATSAPP: WhatsappMark,
  CALL: CallMark,
};

export interface SupportChannelListProps {
  channels: SupportChannel[];
  /**
   * Writes each channel's name beside its icon. Set where there is room for
   * the words — the phone's detail sheet gives a whole line to this, while the
   * table's column is one of six and takes the icons alone.
   */
  withLabels?: boolean;
}

/**
 * The channels a plan is supported over, one icon each.
 *
 * Drawn in `SUPPORT_CHANNELS`' order rather than the order they were picked in,
 * so the same pair of channels sits the same way everywhere it is listed and a
 * column of them can be read down. A code this app has no icon for is left out
 * — the edit dialog still carries it, so it is hidden here, not lost.
 *
 * Shared by the table and the phone's sheet for the reason
 * `PLAN_STATUS_STYLES` is: the same plan must not arrive in two
 * different hands depending on the width of the screen it is read on.
 */
const SupportChannelList: React.FC<SupportChannelListProps> = ({
  channels,
  withLabels = false,
}) => {
  const shown = SUPPORT_CHANNELS.filter((channel) =>
    channels.includes(channel.value)
  );

  if (shown.length === 0) {
    return (
      <span className="text-[14px] text-ink-subtle">
        {/* An em dash, so this reads as answered-with-none rather than as
            something that failed to render. */}
        —<span className="sr-only">No support channels</span>
      </span>
    );
  }

  return (
    <span
      className={`flex items-center ${
        // Wider apart with the names in, so a name binds to the icon ahead of
        // it rather than to the one it is sitting between.
        withLabels ? "flex-wrap gap-x-4 gap-y-1.5" : "gap-2.5"
      }`}
    >
      {shown.map(({ value, label }) => {
        const Mark = CHANNEL_ICONS[value];
        return (
          <span key={value} className="inline-flex items-center gap-1.5">
            {/* Named only where the name is not already written beside it —
                otherwise the label is read out twice. */}
            <Mark
              aria-hidden={withLabels || undefined}
              role={withLabels ? undefined : "img"}
              aria-label={withLabels ? undefined : label}
              className="h-[18px] w-[18px] flex-shrink-0"
            />
            {withLabels && <span className="text-[14px] text-ink">{label}</span>}
          </span>
        );
      })}
    </span>
  );
};

export default SupportChannelList;
